import { useLoaderData } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { detailCta, DetailTileCard } from '~/ui-rocco/pages/detail-shared';
import type { SimilarProducts } from './use-similar-products';

/**
 * Inner content of the Similar Products block (Figma 1841:9733/10119/10162) —
 * the two-tone "More in {category} category" heading, the category breadcrumb,
 * the 2-col (mobile) / 4-col (desktop) tile grid and the "Load More" pill. No
 * eyebrow / hairline / leading spacer: the standalone section and the accordion
 * row each supply their own surrounding chrome.
 */
export const SimilarProductsGrid = ({
    tiles,
    hasMore,
    isLoading,
    loadMore,
    hideLoadMore = false,
}: SimilarProducts & {
    /** Suppress the inline pill when the Magic Island owns "Load More". */
    hideLoadMore?: boolean;
}) => {
    const { product, primaryCategory } = useLoaderData<typeof loader>();

    return (
        <>
            <h2 className="m-0 font-sans font-normal text-[#1f1f21] max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em] md:text-[12.5rem] md:leading-[0.9] md:tracking-[-0.042em]">
                More in{' '}
                <span className="text-[#767676] md:text-[#8f8f8f]">
                    {primaryCategory?.PrettyTitle}
                </span>{' '}
                category
            </h2>

            {/* Breadcrumb sub-line (Figma 1841:10162): the category trail, last
                crumb emphasised. */}
            {product.Breadcrumbs.length > 0 ? (
                <nav
                    aria-label="Category breadcrumb"
                    // Figma 2354:58948: 22px, #181818, leaf medium; 160px below
                    // the heading.
                    className="flex flex-wrap items-center gap-x-2 font-sans text-[#181818] max-md:mt-3 max-md:text-sm md:mt-[10rem] md:text-[1.375rem]"
                >
                    {product.Breadcrumbs.map((crumb, index) => {
                        const isLast = index === product.Breadcrumbs.length - 1;
                        // Figma shows the root as "Product" (not "Product library").
                        const label =
                            crumb.label === 'Product library'
                                ? 'Product'
                                : crumb.label;
                        return (
                            <span
                                key={crumb.href}
                                className="flex items-center gap-x-2"
                            >
                                <RoccoLink
                                    to={crumb.href}
                                    className={cn(
                                        'transition-opacity hover:opacity-60',
                                        isLast && 'font-medium',
                                    )}
                                >
                                    {/* Leaf crumb is parenthesised per the Figma
                                        (node 2354:58948 "… / (Seating)"). */}
                                    {isLast ? `(${label})` : label}
                                </RoccoLink>
                                {isLast ? null : (
                                    <span aria-hidden="true">/</span>
                                )}
                            </span>
                        );
                    })}
                </nav>
            ) : null}

            <div className="grid grid-cols-2 max-md:mt-6 max-md:gap-x-3.5 max-md:gap-y-[3.6875rem] md:mt-[2.125rem] md:grid-cols-4 md:gap-x-[1.5rem] md:gap-y-[3rem]">
                {tiles.map((tile) => (
                    <DetailTileCard key={tile.id} tile={tile} />
                ))}
            </div>

            {hasMore && !hideLoadMore ? (
                <div className="mt-[3rem] flex justify-center max-md:mt-10">
                    <button
                        type="button"
                        onClick={loadMore}
                        disabled={isLoading}
                        className={detailCta(
                            'filled-light',
                            'disabled:opacity-50 max-md:h-[3.125rem] max-md:w-full md:min-w-[14.4rem]',
                        )}
                    >
                        {isLoading ? 'Loading…' : 'Load More'}
                    </button>
                </div>
            ) : null}
        </>
    );
};
